if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        // prevent duplicate listeners on reinit
        if (this._submitHandler && this.form) {
          this.form.removeEventListener("submit", this._submitHandler);
        }

        this.form = this.querySelector("form");

        if (this.form) {
          this.form.querySelector('[name="id"]').disabled = false;
          this._submitHandler =
            this._submitHandler || this.onSubmitHandler.bind(this);
          this.form.addEventListener("submit", this._submitHandler);
        }

        this.cart = document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');

        const formId = this.form?.getAttribute("id");
        this.submitButtons = formId
          ? Array.from(
              document.querySelectorAll(
                `#${CSS.escape(formId)} [type="submit"], [form="${formId}"][type="submit"]`
              )
            )
          : this.submitButton
          ? [this.submitButton]
          : [];

        if (this.cart && this.submitButton) {
          this.submitButton.setAttribute("aria-haspopup", "dialog");
        }

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.init();

        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButtons.forEach((btn) => {
          btn.setAttribute("aria-disabled", true);
          btn.classList.add("loading");
          btn
            .querySelector(".loading-overlay__spinner")
            ?.classList.remove("hidden");
        });

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        formData.set("quantity", this.getValidatedQuantity());
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              document.dispatchEvent(
                new CustomEvent("cart:error", {
                  detail: {
                    source: "product-form",
                    productVariantId: formData.get("id"),
                    errors: response.description,
                    message: response.message,
                  },
                })
              );
              // -------------------------------------------
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // VARIANT ADDED EVENT
            document.dispatchEvent(
              new CustomEvent("variant:add", {
                detail: {
                  variant: {
                    id: formData.get("id"),
                  },
                  quantity: Number(formData.get("quantity") || 1),
                  formElement: this.form,
                  sectionId: this.dataset.source,
                },
              })
            );
            // -------------------------------------------

            fetch(`${routes.cart_url}.js`)
              .then((response) => {
                return response.text();
              })
              .then((state) => {
                const parsedState = JSON.parse(state);

                // dispatch cart:change for the entire basket
                document.dispatchEvent(
                  new CustomEvent("cart:change", {
                    detail: {
                      cart: parsedState,
                      sectionId: this.dataset.source,
                    },
                  })
                );
                // dispatch cart:change for the entire basket
              })
              .catch((error) => {
                console.error("Error fetching cart state:", error);
              });

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
              });
            }
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            this.submitButtons.forEach((btn) => {
              btn.classList.remove("loading");
              if (!this.error) btn.removeAttribute("aria-disabled");
              btn
                .querySelector(".loading-overlay__spinner")
                ?.classList.add("hidden");
            });
          });
      }

      getValidatedQuantity() {
        const quantityInput = this.form.querySelector('input[name="quantity"]');

        // No quantity selector rendered (e.g. hidden): fall back to the
        // variant's minimum quantity rule exposed on the component, then 1.
        if (!quantityInput) {
          return Number(this.dataset.quantityDefault) || 1;
        }

        const min =
          Number(quantityInput.min) ||
          Number(quantityInput.dataset.min) ||
          1;
        const step = Number(quantityInput.step) || 1;
        const hasMax = quantityInput.max !== "" && quantityInput.max != null;
        const max = hasMax ? Number(quantityInput.max) : Infinity;

        let value = parseInt(quantityInput.value, 10);
        if (Number.isNaN(value)) value = min;

        // Clamp to the allowed range.
        value = Math.min(max, Math.max(min, value));

        // Snap up to the nearest value aligned to the step increment.
        if (step > 1 && (value - min) % step !== 0) {
          value = min + Math.ceil((value - min) / step) * step;
          value = Math.min(max, value);
        }

        // Reflect the corrected value back into the input so the UI matches
        // what is actually submitted.
        if (String(value) !== quantityInput.value) {
          quantityInput.value = String(value);
          quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
        }

        return value;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}

if (!customElements.get("price-per-item")) {
  customElements.define(
    "price-per-item",
    class PricePerItem extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
      }

      connectedCallback() {
        this.pricePerItemText = this.querySelector(".price-per-item__text");
        this.productForm = this.closest("product-form");
        this.quantityInput = this.productForm?.querySelector(
          'input[name="quantity"]'
        );

        this.parsePriceBreaks();
        this.attachEventListeners();
        this.updatePriceDisplay();
      }

      disconnectedCallback() {
        this.abortController.abort();
      }

      parsePriceBreaks() {
        const minQuantity = Number(this.dataset.minQuantity || 1);
        const useCurrencyCode = this.dataset.showCurrencyCode === "true";
        const basePrice = useCurrencyCode
          ? this.dataset.variantPriceWithCurrency
          : this.dataset.variantPrice;

        this.priceBreaks = [];

        if (basePrice) {
          this.priceBreaks.push({
            quantity: minQuantity,
            price: basePrice,
          });
        }

        if (!this.dataset.priceBreaks) return;

        try {
          const parsedBreaks = JSON.parse(this.dataset.priceBreaks);

          parsedBreaks.forEach((priceBreak) => {
            const formattedPrice = useCurrencyCode
              ? priceBreak.price_with_currency
              : priceBreak.price;

            if (!formattedPrice) return;

            this.priceBreaks.push({
              quantity: Number(priceBreak.quantity),
              price: formattedPrice,
            });
          });

          this.priceBreaks.sort((firstTier, secondTier) => {
            return secondTier.quantity - firstTier.quantity;
          });
        } catch (error) {
          console.error("Failed to parse volume pricing tiers", error);
        }
      }

      attachEventListeners() {
        if (!this.quantityInput) return;

        const { signal } = this.abortController;

        this.quantityInput.addEventListener("input", () => {
          this.updatePriceDisplay();
        }, { signal });

        this.quantityInput.addEventListener("change", () => {
          this.updatePriceDisplay();
        }, { signal });

        document.addEventListener("cart:change", (event) => {
          this.handleCartChange(event);
        }, { signal });
      }

      handleCartChange(event) {
        if (!this.quantityInput || !this.productForm) return;

        const variantInput = this.productForm.querySelector('input[name="id"]');
        const currentVariantId = variantInput?.value;
        const cartItems = event.detail?.cart?.items;

        if (!currentVariantId || !Array.isArray(cartItems)) return;

        const cartItem = cartItems.find((item) => {
          return String(item.variant_id) === String(currentVariantId);
        });

        this.quantityInput.dataset.cartQuantity = String(cartItem?.quantity || 0);
        this.updatePriceDisplay();
      }

      getCurrentQuantity() {
        if (!this.quantityInput) return Number(this.dataset.minQuantity || 1);

        const cartQuantity = Number(this.quantityInput.dataset.cartQuantity || 0);
        const inputQuantity = Number(this.quantityInput.value || this.dataset.minQuantity || 1);

        return cartQuantity + inputQuantity;
      }

      updatePriceDisplay() {
        if (!this.pricePerItemText || !this.priceBreaks?.length) return;

        const quantity = this.getCurrentQuantity();
        const matchingTier = this.priceBreaks.find((priceBreak) => {
          return quantity >= priceBreak.quantity;
        });

        if (!matchingTier) return;

        this.pricePerItemText.textContent = `${this.dataset.atText} ${matchingTier.price}/${this.dataset.eachText}`;
      }
    }
  );
}
